import { defineStore } from "pinia";
import { ref } from "vue";
import { UsersAdminService, } from "../infrastructure/adapters/UsersAdminService";
import { RolesService } from "../infrastructure/adapters/RolesService";
import { logger } from "@/infrastructure/services/LoggerService";
export const useUsersAdminStore = defineStore("usersAdmin", () => {
    const service = new UsersAdminService();
    const users = ref([]);
    const totalItems = ref(0);
    const totalPages = ref(0);
    const currentPage = ref(1);
    const isLoading = ref(false);
    const error = ref(null);
    const currentUser = ref(null);
    const isLoadingUser = ref(false);
    const userError = ref(null);
    const isSavingRoles = ref(false);
    const rolesError = ref(null);
    let loadSeq = 0;
    /**
     * Resolves `false`, touching no state, when a later call superseded this one; only the latest
     * call writes the list, the error and the loading flag, and only its failure rejects.
     */
    async function loadUsers(params) {
        const seq = ++loadSeq;
        isLoading.value = true;
        error.value = null;
        try {
            const page = await service.list(params);
            if (seq !== loadSeq)
                return false;
            users.value = page.items;
            totalItems.value = page.totalCount;
            totalPages.value = page.totalPages;
            currentPage.value = page.pageNumber;
            return true;
        }
        catch (err) {
            if (seq !== loadSeq)
                return false;
            error.value = err instanceof Error ? err.message : "accessManager.users.loadFailed";
            throw err;
        }
        finally {
            if (seq === loadSeq)
                isLoading.value = false;
        }
    }
    let loadUserSeq = 0;
    /**
     * `keepCurrent` is for a refresh after a write on the open record only: clearing the record
     * unmounts the detail view's content and dialogs mid-flow. Every other load clears it, because
     * the store outlives the view and a new visit must not show the previous visit's data.
     *
     * Like `loadUsers`, only the latest call writes the record, the error and the loading flag: a
     * refresh of user A still in flight after navigating to user B resolves without touching state.
     */
    async function loadUser(userId, { keepCurrent = false } = {}) {
        const seq = ++loadUserSeq;
        isLoadingUser.value = true;
        userError.value = null;
        if (!keepCurrent)
            currentUser.value = null;
        try {
            const user = await service.getById(userId);
            if (seq === loadUserSeq)
                currentUser.value = user;
        }
        catch (err) {
            if (seq !== loadUserSeq)
                return;
            userError.value = err instanceof Error ? err.message : "accessManager.userDetail.loadFailed";
            throw err;
        }
        finally {
            if (seq === loadUserSeq)
                isLoadingUser.value = false;
        }
    }
    // ── Admin actions (Lot 4) ───────────────────────────────────────────────────
    // Thin mutations: each rethrows on failure so the calling view/composable maps the
    // BFF errorCode to a toast (via resolveApiErrorKey). Refreshing the affected data
    // (list re-fetch vs loadUser) is the caller's concern — the list owns its query
    // params and the detail view owns its userId, so the store does not reload here.
    async function deactivateUser(userId) {
        await service.deactivate(userId);
    }
    async function reactivateUser(userId) {
        await service.reactivate(userId);
    }
    async function resetPassword(userId) {
        await service.resetPassword(userId);
    }
    async function resendInvitation(userId) {
        await service.resendInvitation(userId);
    }
    async function inviteUsers(emails, roles) {
        return service.inviteUsers(emails, roles);
    }
    async function updateUserRoles(userId, roles) {
        rolesError.value = null;
        // CONSTRAINT: isSavingRoles stays true through the reload. Until it lands the editor's roles
        // prop still holds the old roles, so its Save would re-enable and could send the PUT twice.
        isSavingRoles.value = true;
        try {
            try {
                await service.updateRoles(userId, roles);
            }
            catch (err) {
                rolesError.value =
                    err instanceof Error ? err.message : "accessManager.userDetail.permissions.saveFailed";
                throw err;
            }
            // The write above is persisted: a failed reload must not be reported as a save failure.
            try {
                await loadUser(userId, { keepCurrent: true });
            }
            catch (err) {
                logger.error("Failed to refresh user detail after a successful role save:", err);
            }
        }
        finally {
            isSavingRoles.value = false;
        }
    }
    function resetRolesError() {
        rolesError.value = null;
    }
    // Kept for the session: each permissions dialog opening mounts a fresh editor.
    const roleCatalogue = ref(null);
    let pendingRoleCatalogue = null;
    async function loadRoleCatalogue() {
        if (roleCatalogue.value)
            return;
        pendingRoleCatalogue ?? (pendingRoleCatalogue = new RolesService()
            .getAll()
            .then((roles) => {
            roleCatalogue.value = roles;
        })
            .finally(() => {
            pendingRoleCatalogue = null;
        }));
        return pendingRoleCatalogue;
    }
    return {
        users,
        totalItems,
        totalPages,
        currentPage,
        isLoading,
        error,
        loadUsers,
        currentUser,
        isLoadingUser,
        userError,
        loadUser,
        deactivateUser,
        reactivateUser,
        resetPassword,
        resendInvitation,
        inviteUsers,
        isSavingRoles,
        rolesError,
        resetRolesError,
        updateUserRoles,
        roleCatalogue,
        loadRoleCatalogue,
    };
});
